import { useEffect, useState } from 'react';
import { useTranslation, UseTranslationOptions } from 'react-i18next';
import i18n from '../i18n';

const namespaceLoadPromises = new Map<string, Promise<void>>();

// Test-only: clears the module-level dedup cache so tests don't leak
// loaded-namespace state into one another across `it()` blocks.
export function __resetNamespaceLoadCache() {
  namespaceLoadPromises.clear();
}

/**
 * Custom hook that ensures namespace resources are loaded before rendering
 * @param namespaces - Single namespace or array of namespaces to load
 * @param options - Additional i18next options
 * @returns Translation function and loading state
 */
export function useNamespacedTranslation(
  namespaces: string | string[],
  options?: UseTranslationOptions<any>
) {
  const ns = Array.isArray(namespaces) ? namespaces : [namespaces];
  // Ensure the namespaces are evaluated in the order provided (i18next requires nsMode: 'fallback')
  const translationOptions: UseTranslationOptions<any> = {
    nsMode: ns.length > 1 ? 'fallback' : undefined,
    ...options,
  };

  const { t, i18n: i18nInstance, ready } = useTranslation(ns, translationOptions);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    let mounted = true;

    const loadNamespaces = async () => {
      const currentLang = i18nInstance.language?.split('-')[0] || 'en';
      
      try {
        // Load all requested namespaces
        await Promise.all(
          ns.map(async (namespace) => {
            // Check if namespace is already loaded
            const hasNamespace = i18nInstance.hasResourceBundle(currentLang, namespace);
            
            if (!hasNamespace) {
              // Manually load the namespace
              const cacheKey = `${currentLang}:${namespace}`;
              let loadPromise = namespaceLoadPromises.get(cacheKey);
              if (!loadPromise) {
                loadPromise = new Promise<void>((resolve, reject) => {
                i18nInstance.loadNamespaces(namespace, (err) => {
                  if (err) {
                    console.warn(`[useNamespacedTranslation] Failed to load namespace "${namespace}":`, err);
                    reject(err);
                  } else {
                    console.log(`[useNamespacedTranslation] Successfully loaded namespace "${namespace}" for language "${currentLang}"`);
                    resolve();
                  }
                });
              });
                namespaceLoadPromises.set(cacheKey, loadPromise);
              }
              await loadPromise;
            }
          })
        );

        if (mounted) {
          setIsLoaded(true);
        }
      } catch (error) {
        // Not routed through useErrorHandler: this hook is used by nearly every component in
        // the app (including in dozens of tests that render components without mocking
        // useErrorHandler or wrapping ErrorProvider), so adding that dependency here would be
        // extremely high-blast-radius for a namespace-load failure that already degrades
        // gracefully below (falls back to already-loaded/default namespace strings instead of
        // hanging on a loading state).
        console.error('[useNamespacedTranslation] Error loading namespaces:', error);
        // Still set loaded to true to prevent infinite loading
        if (mounted) {
          setIsLoaded(true);
        }
      }
    };

    loadNamespaces();

    return () => {
      mounted = false;
    };
  }, [i18nInstance, i18nInstance.language, ns.join(',')]);

  return {
    t,
    i18n: i18nInstance,
    ready: ready && isLoaded,
    isLoaded,
  };
}

/**
 * Preload namespaces for a specific language
 * Useful for prefetching translations before they're needed
 */
export async function preloadNamespaces(
  namespaces: string | string[],
  language?: string
): Promise<void> {
  const ns = Array.isArray(namespaces) ? namespaces : [namespaces];
  const lng = language || i18n.language?.split('-')[0] || 'en';

  await Promise.all(
    ns.map(async (namespace) => {
      const hasNamespace = i18n.hasResourceBundle(lng, namespace);
      
      if (!hasNamespace) {
        const cacheKey = `${lng}:${namespace}`;
        let loadPromise = namespaceLoadPromises.get(cacheKey);
        if (!loadPromise) {
          loadPromise = new Promise<void>((resolve, reject) => {
          i18n.loadNamespaces(namespace, (err) => {
            if (err) {
              console.warn(`[preloadNamespaces] Failed to preload namespace "${namespace}":`, err);
              reject(err);
            } else {
              console.log(`[preloadNamespaces] Successfully preloaded namespace "${namespace}" for language "${lng}"`);
              resolve();
            }
          });
        });
          namespaceLoadPromises.set(cacheKey, loadPromise);
        }
        return loadPromise;
      }
    })
  );
}
